import React, { useState, useRef, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import {
  Upload,
  FileSpreadsheet,
  Loader2,
  CheckCircle2,
  XCircle,
  AlertTriangle,
  ArrowRight,
  ArrowLeft,
  Download,
  Eye,
  Users,
  UserCheck,
  Building2,
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const ImportPage = () => {
  const { user } = useAuth();
  const fileRef = useRef(null);

  const [activeTab, setActiveTab] = useState('import'); // 'import' | 'manage'
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [columnMapping, setColumnMapping] = useState({}); // manual mapping overrides

  // Manage tab state
  const [imported, setImported] = useState([]);
  const [loadingImported, setLoadingImported] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [usersList, setUsersList] = useState([]);
  const [partnersList, setPartnersList] = useState([]);
  const [teamsList, setTeamsList] = useState([]);
  const [bulkAssign, setBulkAssign] = useState({ assigned_to: '', partner_id: '', team_id: '' });
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('todos'); // todos | sem_comercial | sem_parceiro

  const fetchImported = useCallback(async () => {
    setLoadingImported(true);
    try {
      const res = await axios.get(`${API_URL}/contracts/imported`);
      setImported(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingImported(false);
    }
  }, []);

  const fetchLookups = useCallback(async () => {
    try {
      const [u, p, t] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/partners`),
        axios.get(`${API_URL}/teams`),
      ]);
      setUsersList(u.data);
      setPartnersList(p.data);
      setTeamsList(t.data);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'manage') {
      fetchImported();
      fetchLookups();
    }
  }, [activeTab, fetchImported, fetchLookups]);

  const handleFileSelect = (e) => {
    const f = e.target.files[0];
    if (f) {
      setFile(f);
      setPreview(null);
      setResult(null);
      setStep(1);
    }
  };

  const handlePreview = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API_URL}/contracts/import/preview`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setPreview(res.data);
      setColumnMapping(res.data.column_mapping || {});
      setStep(2);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao processar ficheiro');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('column_mapping', JSON.stringify(columnMapping));
      const res = await axios.post(`${API_URL}/contracts/import/execute`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setResult(res.data);
      setStep(3);
      toast.success(`${res.data.imported} contratos importados!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro na importacao');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setFile(null);
    setPreview(null);
    setResult(null);
    setColumnMapping({});
    setStep(1);
    if (fileRef.current) fileRef.current.value = '';
  };

  // Filtered list
  const filteredImported = imported.filter(c => {
    if (filter === 'sem_comercial') return !c.assigned_to;
    if (filter === 'sem_parceiro') return !c.partner_id;
    return true;
  });

  // Bulk assign handlers
  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const toggleAll = () => {
    if (selectedIds.length === filteredImported.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredImported.map(c => c.id));
    }
  };

  const handleBulkAssign = async () => {
    if (selectedIds.length === 0) {
      toast.error('Selecione pelo menos um contrato');
      return;
    }
    const payload = { contract_ids: selectedIds };
    if (bulkAssign.assigned_to) payload.assigned_to = bulkAssign.assigned_to;
    if (bulkAssign.partner_id) payload.partner_id = bulkAssign.partner_id;
    if (bulkAssign.team_id) payload.team_id = bulkAssign.team_id;

    if (!payload.assigned_to && !payload.partner_id && !payload.team_id) {
      toast.error('Selecione pelo menos um campo para atribuir');
      return;
    }

    setSaving(true);
    try {
      const res = await axios.post(`${API_URL}/contracts/bulk-assign`, payload);
      toast.success(`${res.data.updated} contratos atualizados!`);
      setSelectedIds([]);
      setBulkAssign({ assigned_to: '', partner_id: '', team_id: '' });
      fetchImported();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao atribuir');
    } finally {
      setSaving(false);
    }
  };

  if (!['super_admin', 'gestor'].includes(user?.role)) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96 text-slate-400">Sem permissao</div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 max-w-6xl" data-testid="import-page">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-outfit flex items-center gap-2">
            <Upload className="w-6 h-6 text-[#2D3E6F]" />
            Importar Contratos
          </h1>
          <p className="text-slate-500 mt-1">Importar contratos e gerir atribuicoes</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          <button
            onClick={() => setActiveTab('import')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'import'
                ? 'bg-white text-[#2D3E6F] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-import"
          >
            <Upload className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Importar Ficheiro
          </button>
          <button
            onClick={() => setActiveTab('manage')}
            className={`px-5 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === 'manage'
                ? 'bg-white text-[#2D3E6F] shadow-sm'
                : 'text-slate-500 hover:text-slate-700'
            }`}
            data-testid="tab-manage"
          >
            <Users className="w-4 h-4 inline mr-1.5 -mt-0.5" />
            Gerir Importados ({imported.length || 0})
          </button>
        </div>

        {/* IMPORT TAB */}
        {activeTab === 'import' && (
          <>
            {/* Step indicator */}
            <div className="flex items-center gap-3">
              {[
                { n: 1, label: 'Carregar Ficheiro' },
                { n: 2, label: 'Pre-visualizar' },
                { n: 3, label: 'Resultado' },
              ].map((s) => (
                <div key={s.n} className="flex items-center gap-2">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                    step >= s.n ? 'bg-[#2D3E6F] text-white' : 'bg-slate-200 text-slate-500'
                  }`}>
                    {step > s.n ? <CheckCircle2 className="w-4 h-4" /> : s.n}
                  </div>
                  <span className={`text-sm ${step >= s.n ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>{s.label}</span>
                  {s.n < 3 && <ArrowRight className="w-4 h-4 text-slate-300 mx-2" />}
                </div>
              ))}
            </div>

            {/* STEP 1: Upload */}
            {step === 1 && (
              <Card className="rounded-2xl border-slate-100">
                <CardContent className="p-8">
                  <div
                    className="border-2 border-dashed border-slate-200 rounded-xl p-12 text-center hover:border-[#2D3E6F]/50 transition-colors cursor-pointer"
                    onClick={() => fileRef.current?.click()}
                    data-testid="drop-zone"
                  >
                    <FileSpreadsheet className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-lg font-medium text-slate-700">
                      {file ? file.name : 'Clique para selecionar o ficheiro'}
                    </p>
                    <p className="text-sm text-slate-400 mt-2">
                      Formatos suportados: .xlsx, .csv
                    </p>
                    {file && (
                      <p className="text-xs text-slate-500 mt-2">
                        {(file.size / 1024).toFixed(1)} KB
                      </p>
                    )}
                    <input
                      ref={fileRef}
                      type="file"
                      data-max-mb="0"
                      accept=".xlsx,.xls,.csv"
                      onChange={handleFileSelect}
                      className="hidden"
                      data-testid="file-input"
                    />
                  </div>
                  <div className="flex justify-between mt-6">
                    <Button
                      variant="outline"
                      onClick={() => {
                        window.open(`${API_URL}/contracts/import/template`, '_blank');
                      }}
                      className="gap-2"
                      data-testid="download-template-btn"
                    >
                      <Download className="w-4 h-4" />
                      Descarregar Template
                    </Button>
                    <Button
                      onClick={handlePreview}
                      disabled={!file || loading}
                      className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2"
                      data-testid="preview-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="w-4 h-4" />}
                      Pre-visualizar
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* STEP 2: Preview */}
            {step === 2 && preview && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="rounded-2xl border-slate-100">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                        <FileSpreadsheet className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-slate-900">{preview.total_rows}</p>
                        <p className="text-xs text-slate-500">Linhas no ficheiro</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-slate-100">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-emerald-600">{preview.valid_count}</p>
                        <p className="text-xs text-slate-500">Prontos a importar</p>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="rounded-2xl border-slate-100">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                      </div>
                      <div>
                        <p className="text-2xl font-bold text-amber-600">{preview.issue_count}</p>
                        <p className="text-xs text-slate-500">Com avisos</p>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Column mapping - editable dropdowns */}
                <Card className="rounded-2xl border-slate-100">
                  <CardHeader className="border-b border-slate-100 pb-3">
                    <CardTitle className="text-base font-semibold">Mapeamento de Colunas</CardTitle>
                    <p className="text-xs text-slate-500 mt-1">Associe cada coluna do ficheiro ao campo correspondente do CRM. As colunas sem associacao serao ignoradas.</p>
                  </CardHeader>
                  <CardContent className="p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                      {(preview.headers || []).map((header) => {
                        const currentValue = columnMapping[header] || '';
                        // Determine which CRM fields are already used by other headers
                        const usedFields = Object.entries(columnMapping)
                          .filter(([h, f]) => h !== header && f)
                          .map(([, f]) => f);
                        return (
                          <div key={header} className="flex flex-col gap-1.5 bg-slate-50 rounded-lg p-3" data-testid={`mapping-row-${header}`}>
                            <span className="text-xs font-mono text-slate-600 truncate font-medium" title={header}>{header}</span>
                            <Select
                              value={currentValue}
                              onValueChange={(val) => {
                                setColumnMapping(prev => {
                                  const updated = { ...prev };
                                  if (val === '__none__') {
                                    delete updated[header];
                                  } else {
                                    updated[header] = val;
                                  }
                                  return updated;
                                });
                              }}
                            >
                              <SelectTrigger className="h-8 text-xs bg-white" data-testid={`mapping-select-${header}`}>
                                <SelectValue placeholder="-- Ignorar --" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="__none__">-- Ignorar --</SelectItem>
                                {(preview.crm_fields || []).map((field) => (
                                  <SelectItem
                                    key={field.key}
                                    value={field.key}
                                    disabled={usedFields.includes(field.key)}
                                  >
                                    {field.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {currentValue && (
                              <span className="text-[10px] text-emerald-600 font-medium flex items-center gap-1">
                                <CheckCircle2 className="w-3 h-3" /> Mapeado
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {Object.keys(columnMapping).length === 0 && (
                      <div className="mt-3 p-3 rounded-lg bg-amber-50 border border-amber-200">
                        <p className="text-xs text-amber-700 flex items-center gap-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          Nenhuma coluna mapeada. Associe pelo menos o nome do cliente e a empresa.
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Issues */}
                {preview.issues.length > 0 && (
                  <Card className="rounded-2xl border-amber-200 bg-amber-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-amber-700 flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" /> Avisos ({preview.issues.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      <ul className="space-y-1">
                        {preview.issues.slice(0, 10).map((iss, i) => (
                          <li key={i} className="text-xs text-amber-700">{iss}</li>
                        ))}
                        {preview.issues.length > 10 && (
                          <li className="text-xs text-amber-500">...e mais {preview.issues.length - 10} avisos</li>
                        )}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* Data preview table */}
                <Card className="rounded-2xl border-slate-100 overflow-hidden">
                  <CardHeader className="border-b border-slate-100 pb-3">
                    <CardTitle className="text-base font-semibold">Pre-visualizacao dos Dados</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/80">
                            <TableHead className="font-semibold text-xs w-10">#</TableHead>
                            <TableHead className="font-semibold text-xs">Cliente</TableHead>
                            <TableHead className="font-semibold text-xs">NIF</TableHead>
                            <TableHead className="font-semibold text-xs">Telefone</TableHead>
                            <TableHead className="font-semibold text-xs">Empresa</TableHead>
                            <TableHead className="font-semibold text-xs">Categoria</TableHead>
                            <TableHead className="font-semibold text-xs">Estado</TableHead>
                            <TableHead className="font-semibold text-xs">Vendedor</TableHead>
                            <TableHead className="font-semibold text-xs">Data</TableHead>
                            <TableHead className="font-semibold text-xs w-10"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {preview.preview.map((row) => (
                            <TableRow key={row.row_num} className={row.issues.length > 0 ? 'bg-amber-50/30' : ''}>
                              <TableCell className="font-mono text-xs text-slate-400">{row.row_num}</TableCell>
                              <TableCell className="text-sm font-medium">{row.mapped.nome_cliente || '-'}</TableCell>
                              <TableCell className="text-xs font-mono">{row.mapped.nif || '-'}</TableCell>
                              <TableCell className="text-xs">{row.mapped.telefone || '-'}</TableCell>
                              <TableCell className="text-sm">
                                <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                                  {row.mapped.company_original || row.mapped.company || '-'}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">{row.mapped.service_category || '-'}</TableCell>
                              <TableCell>
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  row.mapped.status === 'concluido' ? 'bg-emerald-100 text-emerald-700' :
                                  row.mapped.status === 'anulado' ? 'bg-red-100 text-red-700' :
                                  'bg-slate-100 text-slate-600'
                                }`}>
                                  {row.mapped.status || '-'}
                                </span>
                              </TableCell>
                              <TableCell className="text-xs">
                                {row.mapped.assigned_to_name || row.mapped.vendedor_original || '-'}
                                {row.mapped.assigned_to_name && <CheckCircle2 className="w-3 h-3 text-emerald-500 inline ml-1" />}
                              </TableCell>
                              <TableCell className="text-xs text-slate-500">{row.mapped.data_contrato || '-'}</TableCell>
                              <TableCell>
                                {row.issues.length > 0 ? (
                                  <AlertTriangle className="w-4 h-4 text-amber-500" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-500" />
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>

                {/* Actions */}
                <div className="flex justify-between">
                  <Button variant="outline" onClick={reset} className="gap-2">
                    <ArrowLeft className="w-4 h-4" /> Voltar
                  </Button>
                  <Button
                    onClick={handleImport}
                    disabled={loading}
                    className="bg-emerald-600 hover:bg-emerald-700 gap-2"
                    data-testid="import-btn"
                  >
                    {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                    Importar {preview.total_rows} Contratos
                  </Button>
                </div>
              </>
            )}

            {/* STEP 3: Result */}
            {step === 3 && result && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Card className="rounded-2xl border-emerald-200 bg-emerald-50">
                    <CardContent className="p-5 flex items-center gap-4">
                      <div className="w-12 h-12 rounded-xl bg-emerald-200 flex items-center justify-center">
                        <CheckCircle2 className="w-6 h-6 text-emerald-700" />
                      </div>
                      <div>
                        <p className="text-3xl font-bold text-emerald-700">{result.imported}</p>
                        <p className="text-xs text-emerald-600">Importados com sucesso</p>
                      </div>
                    </CardContent>
                  </Card>
                  {result.skipped > 0 && (
                    <Card className="rounded-2xl border-amber-200 bg-amber-50">
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-amber-200 flex items-center justify-center">
                          <AlertTriangle className="w-6 h-6 text-amber-700" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-amber-700">{result.skipped}</p>
                          <p className="text-xs text-amber-600">Ignorados</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                  {result.errors > 0 && (
                    <Card className="rounded-2xl border-red-200 bg-red-50">
                      <CardContent className="p-5 flex items-center gap-4">
                        <div className="w-12 h-12 rounded-xl bg-red-200 flex items-center justify-center">
                          <XCircle className="w-6 h-6 text-red-700" />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-red-700">{result.errors}</p>
                          <p className="text-xs text-red-600">Erros</p>
                        </div>
                      </CardContent>
                    </Card>
                  )}
                </div>

                {result.imported_details?.length > 0 && (
                  <Card className="rounded-2xl border-slate-100">
                    <CardHeader className="border-b border-slate-100 pb-3">
                      <CardTitle className="text-base font-semibold text-emerald-700">Contratos Importados</CardTitle>
                    </CardHeader>
                    <CardContent className="p-0">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-slate-50/80">
                            <TableHead className="text-xs font-semibold">Codigo</TableHead>
                            <TableHead className="text-xs font-semibold">Cliente</TableHead>
                            <TableHead className="text-xs font-semibold">Empresa</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {result.imported_details.map((r, i) => (
                            <TableRow key={i}>
                              <TableCell className="font-mono text-sm">#{r.codigo}</TableCell>
                              <TableCell className="text-sm">{r.cliente}</TableCell>
                              <TableCell className="text-sm">{r.company}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </CardContent>
                  </Card>
                )}

                {result.skipped_details?.length > 0 && (
                  <Card className="rounded-2xl border-amber-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm font-semibold text-amber-700">Ignorados</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                      {result.skipped_details.map((s, i) => (
                        <p key={i} className="text-xs text-amber-700">Linha {s.row}: {s.reason} {s.cliente ? `(${s.cliente})` : ''}</p>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="flex gap-3">
                  <Button variant="outline" onClick={reset} className="gap-2">
                    <Upload className="w-4 h-4" /> Nova Importacao
                  </Button>
                  <Button
                    onClick={() => { setActiveTab('manage'); fetchImported(); fetchLookups(); }}
                    className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2"
                    data-testid="go-manage-btn"
                  >
                    <Users className="w-4 h-4" /> Gerir Atribuicoes
                  </Button>
                  <Button onClick={() => window.location.href = '/contracts/geral'} variant="outline" className="gap-2">
                    Ver Contratos
                  </Button>
                </div>
              </>
            )}
          </>
        )}

        {/* MANAGE TAB */}
        {activeTab === 'manage' && (
          <>
            {/* Filter bar */}
            <div className="flex items-center gap-2">
              {[
                { key: 'todos', label: 'Todos', count: imported.length },
                { key: 'sem_comercial', label: 'Sem comercial', count: imported.filter(c => !c.assigned_to).length },
                { key: 'sem_parceiro', label: 'Sem parceiro', count: imported.filter(c => !c.partner_id).length },
              ].map(f => (
                <button
                  key={f.key}
                  onClick={() => { setFilter(f.key); setSelectedIds([]); }}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                    filter === f.key
                      ? 'bg-[#2D3E6F] text-white'
                      : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                  }`}
                  data-testid={`filter-${f.key}`}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>

            {/* Bulk assign toolbar */}
            {selectedIds.length > 0 && (
              <Card className="rounded-2xl border-[#2D3E6F]/20 bg-[#2D3E6F]/5">
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <UserCheck className="w-5 h-5 text-[#2D3E6F]" />
                    <span className="text-sm font-semibold text-[#2D3E6F]">
                      Atribuir em massa ({selectedIds.length} selecionados)
                    </span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Comercial</label>
                      <Select value={bulkAssign.assigned_to} onValueChange={(v) => setBulkAssign(prev => ({ ...prev, assigned_to: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="bulk-assigned-to">
                          <SelectValue placeholder="Selecionar comercial..." />
                        </SelectTrigger>
                        <SelectContent>
                          {usersList.map(u => (
                            <SelectItem key={u.id} value={u.id}>{u.name} ({u.role})</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Parceiro</label>
                      <Select value={bulkAssign.partner_id} onValueChange={(v) => setBulkAssign(prev => ({ ...prev, partner_id: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="bulk-partner">
                          <SelectValue placeholder="Selecionar parceiro..." />
                        </SelectTrigger>
                        <SelectContent>
                          {partnersList.map(p => (
                            <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-600 mb-1 block">Equipa</label>
                      <Select value={bulkAssign.team_id} onValueChange={(v) => setBulkAssign(prev => ({ ...prev, team_id: v }))}>
                        <SelectTrigger className="h-9 text-sm" data-testid="bulk-team">
                          <SelectValue placeholder="Selecionar equipa..." />
                        </SelectTrigger>
                        <SelectContent>
                          {teamsList.map(t => (
                            <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex justify-end mt-3">
                    <Button
                      onClick={handleBulkAssign}
                      disabled={saving}
                      className="bg-[#2D3E6F] hover:bg-[#1a2a4a] gap-2 h-9"
                      data-testid="bulk-assign-btn"
                    >
                      {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                      Aplicar Atribuicao
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Imported contracts table */}
            <Card className="rounded-2xl border-slate-100 overflow-hidden">
              <CardHeader className="border-b border-slate-100 pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-semibold flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-slate-400" />
                    Contratos Importados
                  </CardTitle>
                  <span className="text-xs text-slate-400">{filteredImported.length} contratos</span>
                </div>
              </CardHeader>
              <CardContent className="p-0">
                {loadingImported ? (
                  <div className="flex items-center justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
                  </div>
                ) : filteredImported.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                    <FileSpreadsheet className="w-12 h-12 mb-3 opacity-50" />
                    <p className="text-sm">{filter === 'todos' ? 'Nenhum contrato importado' : 'Nenhum contrato encontrado com este filtro'}</p>
                    {filter === 'todos' && (
                      <Button variant="link" onClick={() => setActiveTab('import')} className="mt-2 text-[#2D3E6F]">
                        Importar ficheiro
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-slate-50/80">
                          <TableHead className="w-10">
                            <Checkbox
                              checked={selectedIds.length === filteredImported.length && filteredImported.length > 0}
                              onCheckedChange={toggleAll}
                              data-testid="select-all-checkbox"
                            />
                          </TableHead>
                          <TableHead className="font-semibold text-xs">Cod.</TableHead>
                          <TableHead className="font-semibold text-xs">Cliente</TableHead>
                          <TableHead className="font-semibold text-xs">NIF</TableHead>
                          <TableHead className="font-semibold text-xs">Empresa</TableHead>
                          <TableHead className="font-semibold text-xs">Estado</TableHead>
                          <TableHead className="font-semibold text-xs">Comercial</TableHead>
                          <TableHead className="font-semibold text-xs">Parceiro</TableHead>
                          <TableHead className="font-semibold text-xs">Equipa</TableHead>
                          <TableHead className="font-semibold text-xs">Ficheiro</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredImported.map((c) => (
                          <TableRow
                            key={c.id}
                            className={selectedIds.includes(c.id) ? 'bg-blue-50/50' : ''}
                          >
                            <TableCell>
                              <Checkbox
                                checked={selectedIds.includes(c.id)}
                                onCheckedChange={() => toggleSelect(c.id)}
                                data-testid={`select-${c.codigo}`}
                              />
                            </TableCell>
                            <TableCell className="font-mono text-xs text-slate-500">#{c.codigo}</TableCell>
                            <TableCell className="text-sm font-medium">{c.nome_cliente}</TableCell>
                            <TableCell className="text-xs font-mono">{c.nif || '-'}</TableCell>
                            <TableCell>
                              <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                                {c.company_name || c.company}
                              </span>
                            </TableCell>
                            <TableCell>
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                c.status === 'concluido' ? 'bg-emerald-100 text-emerald-700' :
                                c.status === 'anulado' ? 'bg-red-100 text-red-700' :
                                'bg-slate-100 text-slate-600'
                              }`}>
                                {c.status_label || c.status}
                              </span>
                            </TableCell>
                            <TableCell className="text-xs">
                              {c.assigned_to_name ? (
                                <span className="flex items-center gap-1 text-emerald-700">
                                  <CheckCircle2 className="w-3 h-3" /> {c.assigned_to_name}
                                </span>
                              ) : (
                                <span className="text-amber-500 flex items-center gap-1">
                                  <AlertTriangle className="w-3 h-3" /> Sem comercial
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {c.partner_name ? (
                                <span className="text-slate-700">{c.partner_name}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs">
                              {c.team_name ? (
                                <span className="text-slate-700">{c.team_name}</span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </TableCell>
                            <TableCell className="text-xs text-slate-400 truncate max-w-[120px]" title={c.import_source}>
                              {c.import_source}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};
